import * as React from 'react'
import { ArrowDown, RotateCw } from 'lucide-react'

/* Pull-to-refresh for embedded webviews (iPad kiosk shells have no reload
   button). Deliberately conservative for a POS:
   - touch-only (coarse pointer) — desktop is untouched
   - arms ONLY when every scrollable ancestor of the touch point is at the
     top, so flicking a product grid or a dialog list can never trigger it
   - requires a long deliberate pull (THRESHOLD_PX) with a visible indicator;
     releasing early cancels
   - a real location.reload(): that is what recovers a stuck webview (stale
     chunks, dead socket), and the POS session-recovery bootstrap restores
     the cashier's state after it. */

const THRESHOLD_PX = 96
const MAX_PULL_PX = 140
/** Ignore the gesture entirely until the finger moved this far. */
const ARM_PX = 12

function chainIsAtTop(start: EventTarget | null): boolean {
  let el = start instanceof Element ? start : null
  while (el) {
    if (el.scrollTop > 0) return false
    el = el.parentElement
  }
  return (document.scrollingElement?.scrollTop ?? 0) === 0
}

export function PullToRefresh() {
  const [pull, setPull] = React.useState(0)
  const [reloading, setReloading] = React.useState(false)
  const startY = React.useRef<number | null>(null)
  const active = React.useRef(false)

  React.useEffect(() => {
    // Touch devices only — a mouse wheel or trackpad must never trigger this.
    if (!window.matchMedia('(pointer: coarse)').matches) return

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        startY.current = null
        return
      }
      startY.current = chainIsAtTop(e.target) ? e.touches[0].clientY : null
      active.current = false
    }

    const onMove = (e: TouchEvent) => {
      if (startY.current == null || e.touches.length !== 1) return
      const delta = e.touches[0].clientY - startY.current
      if (delta <= ARM_PX) {
        if (active.current) {
          active.current = false
          setPull(0)
        }
        return
      }
      active.current = true
      // Rubber-band: past the threshold the pull gets heavier.
      const eased = delta < THRESHOLD_PX ? delta : THRESHOLD_PX + (delta - THRESHOLD_PX) * 0.35
      setPull(Math.min(eased, MAX_PULL_PX))
    }

    const onEnd = () => {
      if (active.current && startY.current != null) {
        setPull((current) => {
          if (current >= THRESHOLD_PX) {
            setReloading(true)
            window.location.reload()
            return current
          }
          return 0
        })
      } else {
        setPull(0)
      }
      startY.current = null
      active.current = false
    }

    document.addEventListener('touchstart', onStart, { passive: true })
    document.addEventListener('touchmove', onMove, { passive: true })
    document.addEventListener('touchend', onEnd, { passive: true })
    document.addEventListener('touchcancel', onEnd, { passive: true })
    return () => {
      document.removeEventListener('touchstart', onStart)
      document.removeEventListener('touchmove', onMove)
      document.removeEventListener('touchend', onEnd)
      document.removeEventListener('touchcancel', onEnd)
    }
  }, [])

  if (pull <= 0 && !reloading) return null

  const ready = reloading || pull >= THRESHOLD_PX

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-[100] flex justify-center"
      style={{ transform: `translateY(${Math.max(pull - 44, 4)}px)` }}
    >
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-full border bg-background shadow-md transition-colors ${
          ready ? 'text-primary border-primary/40' : 'text-muted-foreground'
        }`}
      >
        {reloading ? (
          <RotateCw className="h-4 w-4 animate-spin" />
        ) : (
          <ArrowDown
            className="h-4 w-4 transition-transform duration-150"
            style={{ transform: ready ? 'rotate(180deg)' : `rotate(${(pull / THRESHOLD_PX) * 180}deg)` }}
          />
        )}
      </div>
    </div>
  )
}
